// Listing-page events shared by the entity listing pages (projects / phases /
// unit-types / countries). Their card_click / view_item_click /
// register_interest_click bodies are identical apart from the entry_point (and
// two load-bearing deviations captured as options below), so one factory
// parameterised by the entry point replaces the four parallel families — the
// same shape as detailAnalytics.
import {logAnalyticsEvent} from './analytics';
import type {AnalyticsEntryPoint} from './events';
import type {TrackedItem} from './shared';

// Page-level context (project/phase id+name) that some listings must attach
// to every card_click/view_item_click on top of the per-item id/name — static
// for the lifetime of a listing page render, so callers pass it per call
// rather than baking it into the module-scope factory call.
export type ListingAnalyticsExtra = Record<string, string | undefined>;

export interface ListingPageAnalytics {
  cardClick: (item: TrackedItem, extra?: ListingAnalyticsExtra) => void;
  viewItemClick: (item: TrackedItem, extra?: ListingAnalyticsExtra) => void;
  registerInterestClick: (item?: TrackedItem) => void;
}

export interface ListingAnalyticsOptions {
  cardEvent?: 'card_click' | 'item_card_click';
  viewExtra?: (item: TrackedItem) => Record<string, string>;
}

export function listingAnalytics(entryPoint: AnalyticsEntryPoint, opts: ListingAnalyticsOptions = {}): ListingPageAnalytics {
  const cardEvent = opts.cardEvent ?? 'card_click';
  return {
    cardClick: (item, extra) => {
      logAnalyticsEvent(cardEvent, {entry_point: entryPoint, item_id: item.id, item_name: item.name, ...extra});
    },
    viewItemClick: (item, extra) => {
      logAnalyticsEvent('view_item_click', {
        entry_point: entryPoint,
        item_id: item.id,
        item_name: item.name,
        ...extra,
        ...opts.viewExtra?.(item),
      });
    },
    registerInterestClick: item => {
      logAnalyticsEvent('register_interest_click', {entry_point: entryPoint, item_id: item?.id, item_name: item?.name});
    },
  };
}
